import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { usePeriod } from "@/context/PeriodContext";
import { money, formatDateAT } from "@/lib/format";
import { prepareCaptureImage, isMobile, CAPTURE_ERRORS } from "@/lib/imageCapture";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Camera, FileText, Type, Mic, Loader2, Sparkles, AlertOctagon,
  CheckCircle2, Radio, ImageIcon, Upload,
} from "lucide-react";

/**
 * Sprint 23 — Smart Capture dialog. Four inputs (photo · pdf · text · voice)
 * → one confirmation screen → single commit endpoint.
 */
export default function SmartCaptureDialog({
  open, onOpenChange, initialTab = "text", onCommitted,
}) {
  const { lang } = useI18n();
  const { isSelectedClosed } = usePeriod();
  const [tab, setTab] = useState(initialTab);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (open) { setTab(initialTab); setDraft(null); }
  }, [open, initialTab]);

  const t = (tr, de) => (lang === "de" ? de : tr);

  const runText = async (text) => {
    if (!text || !text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/capture/text", { text });
      setDraft(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("İşlenemedi", "Nicht verarbeitet"));
    } finally { setBusy(false); }
  };

  const runFile = async (endpoint, file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("İşlenemedi", "Nicht verarbeitet"));
    } finally { setBusy(false); }
  };

  if (isSelectedClosed) {
    // Rare — dashboard already disables buttons; keep as a safety net.
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!max-w-2xl !w-full
                   !left-0 !top-auto !bottom-0 !right-auto !translate-x-0 !translate-y-0
                   !h-[100dvh] !max-h-[100dvh]
                   sm:!left-[50%] sm:!top-[50%] sm:!bottom-auto sm:!right-auto
                   sm:!translate-x-[-50%] sm:!translate-y-[-50%]
                   sm:!h-auto sm:!max-h-[92vh] sm:!w-[calc(100vw-2rem)]
                   !p-0 !gap-0 flex flex-col rounded-none sm:rounded-lg overflow-hidden"
        data-testid="smart-capture-dialog"
      >
        <DialogHeader className="flex-shrink-0 px-4 py-3 sm:px-6 sm:py-4 border-b bg-background">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            {t("Akıllı Kayıt", "Smart Erfassung")}
          </DialogTitle>
        </DialogHeader>

        <div
          className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6"
          style={{ WebkitOverflowScrolling: "touch" }}
          data-testid="smart-capture-body"
        >
          {!draft && (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-4 h-auto p-1 bg-muted/50 rounded-xl">
                <TabsTrigger value="text"  data-testid="sc-tab-text"  className="rounded-lg"><Type   className="w-4 h-4 mr-1" /> {t("Metin", "Text")}</TabsTrigger>
                <TabsTrigger value="photo" data-testid="sc-tab-photo" className="rounded-lg"><Camera className="w-4 h-4 mr-1" /> {t("Fotoğraf", "Foto")}</TabsTrigger>
                <TabsTrigger value="pdf"   data-testid="sc-tab-pdf"   className="rounded-lg"><FileText className="w-4 h-4 mr-1" /> PDF</TabsTrigger>
                <TabsTrigger value="voice" data-testid="sc-tab-voice" className="rounded-lg"><Mic    className="w-4 h-4 mr-1" /> {t("Ses", "Stimme")}</TabsTrigger>
              </TabsList>

              <TabsContent value="text" className="mt-4"><TextTab onRun={runText} busy={busy} lang={lang} /></TabsContent>
              <TabsContent value="photo" className="mt-4"><PhotoTab busy={busy} lang={lang} onRun={(f) => runFile("/capture/image", f)} /></TabsContent>
              <TabsContent value="pdf"   className="mt-4"><FileTab kind="pdf"   busy={busy} lang={lang} onRun={(f) => runFile("/capture/pdf", f)} /></TabsContent>
              <TabsContent value="voice" className="mt-4"><VoiceTab busy={busy} lang={lang} onRun={(f) => runFile("/capture/voice", f)} /></TabsContent>
            </Tabs>
          )}
        </div>

        {draft && (
          <CapturePreview
            draft={draft}
            onCancel={() => setDraft(null)}
            onCommitted={(res) => { onCommitted?.(res); onOpenChange(false); }}
            lang={lang}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------- Text tab ------- */
function TextTab({ onRun, busy, lang }) {
  const [text, setText] = useState("");
  const t = (tr, de) => (lang === "de" ? de : tr);
  const example = lang === "de"
    ? "z.B. „Metro 245 EUR Fleisch heute"
    : "örn. \"Metro 245 EUR et bugün\"";
  return (
    <div className="space-y-3">
      <Label>{t("Doğal dilde yaz", "Freitext")}</Label>
      <Textarea
        value={text} onChange={(e) => setText(e.target.value)}
        placeholder={example} rows={4}
        data-testid="sc-text-input" className="font-mono"
      />
      <div className="flex justify-end">
        <Button onClick={() => onRun(text)} disabled={busy || !text.trim()} data-testid="sc-text-parse">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {t("Analiz Et", "Analysieren")}
        </Button>
      </div>
    </div>
  );
}

/* ------- Photo tab — native camera + gallery on mobile, dropzone on desktop ------- */
const PROGRESS_STEPS = {
  tr: ["Görüntü alındı", "Belge hizalandı", "Yazılar okunuyor", "Vergiler hesaplanıyor", "Ürünler analiz ediliyor", "AI doğruluyor"],
  de: ["Bild empfangen", "Beleg ausgerichtet", "Text wird gelesen", "Steuern berechnet", "Positionen analysiert", "KI verifiziert"],
};

function CaptureProgress({ lang, running }) {
  const steps = PROGRESS_STEPS[lang === "de" ? "de" : "tr"];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!running) return;
    setIdx(0);
    const id = setInterval(() => setIdx((i) => Math.min(steps.length, i + 1)), 700);
    return () => clearInterval(id);
  }, [running, steps.length]);
  return (
    <div className="rounded-lg border bg-muted/30 p-4 space-y-2" data-testid="sc-photo-progress">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Loader2 className="w-4 h-4 animate-spin text-primary" />
        {lang === "de" ? "AI verarbeitet das Dokument…" : "AI Belgeyi Analiz Ediyor…"}
      </div>
      <ul className="text-sm space-y-1">
        {steps.map((s, i) => {
          const done = i < idx;
          const active = i === idx;
          return (
            <li key={i} className={`flex items-center gap-2 ${done ? "text-emerald-700" : active ? "text-foreground" : "text-muted-foreground"}`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : active ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="w-3.5 h-3.5 rounded-full border border-muted-foreground/30" />}
              <span>{s}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function _humanCaptureError(code, lang) {
  const de = {
    unsupported: "Nicht unterstütztes Format. Bitte JPG, PNG oder WEBP.",
    too_large: "Datei zu groß (max. 25 MB). Bitte kleiner aufnehmen.",
    too_small: "Bild ist zu klein — bitte näher rangehen oder erneut fotografieren.",
    too_dark:  "Bild zu dunkel — bitte in besserem Licht neu aufnehmen.",
    decode_fail: "Bild konnte nicht gelesen werden. Bitte erneut versuchen.",
  };
  const tr = {
    unsupported: "Desteklenmeyen dosya türü. Lütfen JPG, PNG veya WEBP kullan.",
    too_large: "Dosya çok büyük (maks 25 MB). Daha küçük bir fotoğraf çek.",
    too_small: "Fotoğraf çok küçük — daha yakından çek veya tekrar dene.",
    too_dark:  "Fotoğraf çok karanlık — daha aydınlık ortamda tekrar çek.",
    decode_fail: "Görüntü okunamadı. Lütfen tekrar dene.",
  };
  return (lang === "de" ? de : tr)[code] || (lang === "de" ? "Fehler beim Bild." : "Görselde hata.");
}

function PhotoTab({ busy, lang, onRun }) {
  const t = (tr, de) => (lang === "de" ? de : tr);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [prepping, setPrepping] = useState(false);
  const mobile = isMobile();

  const handleFile = async (file) => {
    if (!file) return;
    setPrepping(true);
    try {
      const prepared = await prepareCaptureImage(file);
      onRun(prepared);
    } catch (e) {
      const code = e?.message || CAPTURE_ERRORS.DECODE_FAIL;
      toast.error(_humanCaptureError(code, lang));
    } finally {
      setPrepping(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  if (busy || prepping) {
    return <CaptureProgress lang={lang} running />;
  }

  return (
    <div className="space-y-4">
      {mobile ? (
        <div className="grid grid-cols-2 gap-3" data-testid="sc-photo-mobile">
          <Button
            type="button"
            variant="default"
            className="h-24 flex flex-col items-center justify-center gap-2 rounded-xl shadow-elev-2"
            onClick={() => cameraRef.current?.click()}
            data-testid="sc-photo-camera"
          >
            <Camera className="w-6 h-6" />
            <span className="text-sm font-semibold">{t("Fotoğraf Çek", "Foto aufnehmen")}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-24 flex flex-col items-center justify-center gap-2 rounded-xl"
            onClick={() => galleryRef.current?.click()}
            data-testid="sc-photo-gallery"
          >
            <ImageIcon className="w-6 h-6" />
            <span className="text-sm font-semibold">{t("Galeriden Seç", "Aus Galerie")}</span>
          </Button>
        </div>
      ) : (
        <div
          className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/40"}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => galleryRef.current?.click()}
          data-testid="sc-photo-dropzone"
        >
          <Upload className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
          <div className="text-sm font-semibold mb-1">{t("Dosya Seç veya Sürükle-Bırak", "Datei wählen oder ziehen")}</div>
          <div className="text-xs text-muted-foreground">
            {t("Fatura / fiş — JPG · PNG · WEBP (maks 25 MB)", "Rechnung / Beleg — JPG · PNG · WEBP (max 25 MB)")}
          </div>
        </div>
      )}

      {/* Hidden inputs — camera uses capture attribute for direct rear-camera access */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="sc-photo-camera-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid="sc-photo-gallery-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      <div className="text-[11px] text-muted-foreground text-center">
        {t("Fotoğraf otomatik olarak sıkıştırılır ve döndürülür — kalite korunur.", "Foto wird automatisch komprimiert und ausgerichtet — Qualität bleibt.")}
      </div>
    </div>
  );
}

/* ------- File tab (pdf) ------- */
function FileTab({ kind, busy, lang, onRun }) {
  const ref = useRef(null);
  const t = (tr, de) => (lang === "de" ? de : tr);
  const accept = kind === "image" ? "image/jpeg,image/png,image/webp" : "application/pdf";
  const label  = kind === "image" ? t("Fotoğraf seç", "Foto wählen") : t("PDF seç", "PDF wählen");
  const hint   = kind === "image"
    ? t("Fatura / fiş / makbuz — JPG · PNG · WEBP (maks 25MB)", "Rechnung / Quittung — JPG · PNG · WEBP (max 25MB)")
    : t("Tedarikçi PDF faturası (maks 25MB)", "Lieferanten-PDF (max 25MB)");
  return (
    <div className="space-y-3">
      <div>
        <Label>{label}</Label>
        <div className="text-xs text-muted-foreground mt-1">{hint}</div>
      </div>
      <input
        type="file" accept={accept} ref={ref} capture={kind === "image" ? "environment" : undefined}
        data-testid={`sc-file-${kind}`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onRun(f); }}
        className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer"
      />
      {busy && (
        <div className="rounded-lg border p-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> {t("AI analiz ediyor…", "KI verarbeitet…")}
        </div>
      )}
    </div>
  );
}

/* ------- Voice tab ------- */
function VoiceTab({ busy, lang, onRun }) {
  const t = (tr, de) => (lang === "de" ? de : tr);
  const [rec, setRec] = useState(null);
  const [recording, setRecording] = useState(false);
  const chunks = useRef([]);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunks.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        onRun(new File([blob], "voice.webm", { type: "audio/webm" }));
      };
      mr.start();
      setRec(mr); setRecording(true);
    } catch (e) {
      toast.error(lang === "de" ? "Mikrofon nicht verfügbar" : "Mikrofon açılamadı");
    }
  };
  const stop = () => { rec?.stop(); setRecording(false); };
  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground">
        {t("Mikrofona konuş, sonra durdur. Örn. „Bugün Metro'dan 185 euro sebze aldım.\"",
           "Sprich in dein Mikro und stoppe dann. z.B. „Heute Gemüse bei Metro für 185 Euro gekauft.\"")}
      </div>
      <div className="flex items-center justify-center py-6">
        <Button
          size="lg"
          variant={recording ? "destructive" : "default"}
          onClick={recording ? stop : start}
          disabled={busy}
          data-testid="sc-voice-toggle"
          className="h-16 w-16 rounded-full"
        >
          {recording ? <Radio className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
        </Button>
      </div>
      <div className="text-xs text-center text-muted-foreground">
        {busy ? t("Transkript & analiz…", "Transkription & Analyse…") : recording ? t("Kayıtta…", "Aufnahme…") : t("Başlatmak için dokun", "Zum Starten tippen")}
      </div>
    </div>
  );
}

/* ------- Preview / confirmation ------- */
function ConfidenceChip({ v }) {
  const c = Number(v || 0);
  const tone =
    c >= 0.95 ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
    c >= 0.80 ? "bg-amber-100 text-amber-800 border-amber-300" :
                "bg-red-100 text-red-800 border-red-300";
  return (
    <Badge variant="outline" className={`ml-2 h-4 text-[9px] px-1 ${tone}`}>
      {Math.round(c * 100)}%
    </Badge>
  );
}

function CapturePreview({ draft, onCancel, onCommitted, lang }) {
  const t = (tr, de) => (lang === "de" ? de : tr);
  const [d, setD] = useState(draft);
  const [saving, setSaving] = useState(false);
  const [syncInv, setSyncInv] = useState(false);
  const [remember, setRemember] = useState(true);
  // Sprint 29.4 — OCR classification default. Use the LLM's
  // `suggested_kind` when present, fall back to legacy `kind`.
  const [kind, setKind] = useState(draft.suggested_kind || draft.kind || "expense");
  const conf = d.confidence || {};
  const dup = d.duplicate_of;
  const hasLines = Array.isArray(d.line_items) && d.line_items.length > 0;
  const needsConfirm = (conf.overall || 0) < 0.8;
  const docType = d.document_type || (kind === "invoice" ? "supplier_invoice" : "expense_receipt");
  const docTypeLabel = {
    expense_receipt:  { tr: "Küçük fiş / kasa",        de: "Kleiner Beleg / Kasse" },
    supplier_invoice: { tr: "Tedarikçi faturası",     de: "Lieferantenrechnung" },
    utility_invoice:  { tr: "Fatura (elektrik/su/gaz)", de: "Versorger-Rechnung" },
    rent_invoice:     { tr: "Kira faturası",          de: "Miete / Pacht" },
    other:            { tr: "Diğer",                    de: "Sonstige" },
  }[docType]?.[lang] || docType;

  const set = (k, v) => setD((prev) => ({ ...prev, [k]: v }));

  const commit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/capture/commit", {
        draft: d,
        kind,
        sync_inventory: syncInv,
        remember_preference: remember,
      });
      toast.success(t("Kaydedildi", "Gespeichert"));
      onCommitted?.(data);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.code === "duplicate_invoice") {
        toast.error(t("Bu fatura zaten var — kaydedilmedi.", "Rechnung existiert bereits — nicht gespeichert."));
      } else if (det?.code === "period_closed") {
        toast.error(t(`Ay kapalı (${det.period_key}) — kayıt eklenemez.`, `Monat geschlossen (${det.period_key}).`));
      } else {
        toast.error(typeof det === "string" ? det : (det?.message || t("Kaydedilemedi", "Speichern fehlgeschlagen")));
      }
    } finally { setSaving(false); }
  };

  return (
    <>
      <div
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 space-y-4"
        style={{ WebkitOverflowScrolling: "touch" }}
        data-testid="sc-preview"
      >
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">
            {t("Onay Ekranı", "Bestätigung")}
            {needsConfirm && (
              <Badge variant="outline" className="ml-2 border-amber-300 text-amber-700">
                <AlertOctagon className="w-3 h-3 mr-1" /> {t("Düşük güven", "Niedrige Konfidenz")}
              </Badge>
            )}
          </div>
          <Badge variant="outline" className="font-mono">{d.period_key}</Badge>
        </div>

        {dup && (
          <div className="rounded-lg border border-destructive/40 bg-red-50 text-red-800 px-3 py-2 text-sm flex items-center gap-2" data-testid="sc-dup-warning">
            <AlertOctagon className="w-4 h-4" />
            {t("Bu fatura zaten kayıtlı:", "Diese Rechnung existiert bereits:")}
            <span className="font-mono">{dup.number || dup.party_name} · {money(dup.amount_total)} · {formatDateAT(dup.issue_date)}</span>
          </div>
        )}

        {/* Sprint 29.4 — Prominent "Save as" segmented control */}
        <div className="rounded-xl border bg-gradient-to-br from-primary/5 to-transparent p-3 space-y-2"
             data-testid="sc-save-as">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold">
              {t("Bu belge nasıl kaydedilsin?", "Wie soll dieses Dokument gespeichert werden?")}
            </div>
            <Badge variant="secondary" className="rounded-md text-[10px]" data-testid="sc-doc-type-badge">
              {docTypeLabel}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setKind("expense")}
              data-testid="sc-save-as-expense"
              data-active={kind === "expense"}
              className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-all
                          ${kind === "expense"
                            ? "border-primary bg-primary text-primary-foreground shadow-elev-2"
                            : "hover:border-primary/60 bg-background"}`}
            >
              {t("Gider", "Ausgabe")}
              <div className="text-[10px] font-normal opacity-80">
                {t("Küçük fiş / kasa alışverişi", "Kleiner Beleg / Kasse")}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setKind("invoice")}
              data-testid="sc-save-as-invoice"
              data-active={kind === "invoice"}
              className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-all
                          ${kind === "invoice"
                            ? "border-primary bg-primary text-primary-foreground shadow-elev-2"
                            : "hover:border-primary/60 bg-background"}`}
            >
              {t("Fatura / Ödenecek", "Rechnung / AP")}
              <div className="text-[10px] font-normal opacity-80">
                {t("Tedarikçi, kira, fatura", "Lieferant, Miete, Rechnung")}
              </div>
            </button>
          </div>
          {draft.suggested_kind && kind !== draft.suggested_kind && (
            <div className="text-[10px] text-amber-700 flex items-center gap-1" data-testid="sc-override-hint">
              <AlertOctagon className="w-3 h-3" />
              {t(
                `AI önerisi: ${draft.suggested_kind === "invoice" ? "Fatura" : "Gider"} — kullanıcı seçimi geçerlidir.`,
                `KI-Vorschlag: ${draft.suggested_kind === "invoice" ? "Rechnung" : "Ausgabe"} — Benutzerauswahl bindend.`
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t("Tarih", "Datum")}<ConfidenceChip v={conf.date} /></Label>
            <Input type="date" value={d.date || ""} onChange={(e) => set("date", e.target.value)} className="mt-1 font-mono" data-testid="sc-date" />
          </div>
          <div>
            <Label className="text-xs">{t("Tutar", "Betrag")}<ConfidenceChip v={conf.amount} /></Label>
            <Input type="number" step="0.01" value={d.amount ?? 0} onChange={(e) => set("amount", parseFloat(e.target.value) || 0)} className="mt-1 font-mono" data-testid="sc-amount" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t("Tedarikçi / Taraf", "Lieferant / Partei")}<ConfidenceChip v={conf.vendor} /></Label>
            <Input value={d.vendor || ""} onChange={(e) => set("vendor", e.target.value)} className="mt-1" data-testid="sc-vendor" />
          </div>
          <div>
            <Label className="text-xs">{t("KDV", "USt.")}<ConfidenceChip v={conf.tax_amount} /></Label>
            <Input type="number" step="0.01" value={d.tax_amount ?? 0} onChange={(e) => set("tax_amount", parseFloat(e.target.value) || 0)} className="mt-1 font-mono" data-testid="sc-tax" />
          </div>
        </div>

        {kind === "invoice" && (
          <div>
            <Label className="text-xs">{t("Fatura No", "Rechnungsnummer")}</Label>
            <Input value={d.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} className="mt-1 font-mono" data-testid="sc-inv-number" />
          </div>
        )}

        <div>
          <Label className="text-xs">{t("Açıklama", "Beschreibung")}</Label>
          <Input value={d.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1" data-testid="sc-desc" />
        </div>

        {hasLines && (
          <div className="border rounded-lg" data-testid="sc-lines">
            <div className="px-3 py-2 border-b text-xs uppercase tracking-widest text-muted-foreground flex items-center justify-between sticky top-0 bg-background z-10">
              <span>{t("Kalemler", "Positionen")} <span className="font-mono">({d.line_items.length})</span></span>
              <label className="flex items-center gap-2 text-xs normal-case tracking-normal">
                <Checkbox checked={syncInv} onCheckedChange={(v) => setSyncInv(!!v)} data-testid="sc-inv-sync" />
                {t("Stoku güncelle", "Bestand aktualisieren")}
              </label>
            </div>
            {/*
              The outer preview body handles scrolling; the line list
              renders in full so users can review every position on
              long invoices without a nested scroll trap.
            */}
            <ul className="divide-y">
              {d.line_items.map((li, i) => (
                <li key={i} className="px-3 py-1.5 text-sm flex items-center justify-between gap-2"
                    data-testid={`sc-line-${i}`}>
                  <span className="truncate">{li.name}</span>
                  <span className="font-mono text-xs text-muted-foreground shrink-0">
                    {li.qty}{li.unit ? ` ${li.unit}` : ""} · {money(li.total || li.unit_price * li.qty)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {d.transcript && (
          <div className="border rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="sc-transcript">
            <div className="uppercase tracking-widest mb-1">{t("Transkript", "Transkript")}</div>
            {d.transcript}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} id="sc-remember" data-testid="sc-remember" />
          <label htmlFor="sc-remember" className="text-xs text-muted-foreground">
            {t("Bu tedarikçi tercihini hatırla", "Diese Lieferantenpräferenz merken")}
          </label>
        </div>
      </div>

      {/* Sticky footer — always visible even on long invoices */}
      <div
        className="flex-shrink-0 flex justify-end gap-2 px-4 py-3 sm:px-6 border-t bg-background/95 backdrop-blur
                   [padding-bottom:calc(env(safe-area-inset-bottom)+0.75rem)]"
        data-testid="sc-footer"
      >
        <Button variant="outline" onClick={onCancel} data-testid="sc-cancel" className="min-h-[44px]">
          {t("Vazgeç", "Abbrechen")}
        </Button>
        <Button onClick={commit} disabled={saving || (dup && kind === "invoice")} data-testid="sc-commit"
                className="min-h-[44px]">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          {t("Kaydet", "Speichern")}
        </Button>
      </div>
    </>
  );
}
